import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { useMemo } from 'react';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import { isInternalContent } from 'academy-common-lib/features/learningContent/utils';
import { unwrapLessonChirpError } from 'academy-common-lib/features/lessons/lessonUtils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { ChapterAssetTypename, ExerciseAssetTypename, LinkAssetTypename, QuizAssetTypename, VideoAssetTypename } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';

//@ts-expect-error Could not find declaration file for module
import chapterThumbnailUrl from 'bender-url!../../../images/pngs/chapter_thumbnail.png';
//@ts-expect-error Could not find declaration file for module
import practicalExerciseThumbnailUrl from 'bender-url!../../../images/pngs/practical_exercise_thumbnail.png';
//@ts-expect-error Could not find declaration file for module
import quizThumbnailUrl from 'bender-url!../../../images/pngs/quiz_thumbnail.png';
import getLang from 'LearningCenterUI/utils/getLang';
import InstructorList from '../../components/InstructorList';
import UsageTracker from '../../utils/UsageTracker';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import CardMenuIcon from 'academy-common-lib/features/cardMenu/components/CardMenuIcon';
import { useHasGate } from '../../features/accounts/accountsQueries';
import { useFullLessonChirp } from '../../features/lessons/lessonQueries';
import { isVideoAsset } from '../../features/tracks/tracksUtils';
import { maybeLessonCardModelWithProgress } from '../../features/lessons/lessonUtils';
import { OverviewContent } from '../../features/overview/components/OverviewContent';
import { OverviewModalCurriculumSection } from '../../features/overview/components/OverviewModalCurriculumSection';
import { OverviewModalDescriptionSection } from '../../features/overview/components/OverviewModalDescriptionSection';
import { useOverviewNavigation } from '../../features/overview/overviewRouting';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../hooks/searchParamHooks';
import { getLanguagesFromLinkedContent } from '../../utils/languageUtils';
import { mapProfessorDescriptions } from '../../features/lessons/lessonUtils';
import { useLessonQuizMeVideoRequestParams } from '../../features/interactiveEducation/hooks/useLessonQuizMe';
import { getLessonPreAssessmentUrl } from '../../utils/RoutingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getFallbackThumbnailUrl = typename => {
  switch (typename) {
    case ChapterAssetTypename:
      return chapterThumbnailUrl;
    case ExerciseAssetTypename:
      return practicalExerciseThumbnailUrl;
    case QuizAssetTypename:
      return quizThumbnailUrl;
    default:
      return undefined;
  }
};
const LessonOverviewModalContainer = ({
  isPlaylistOverview,
  language: languageFromProps,
  lessonId,
  source
}) => {
  var _lesson$languageLinke;
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const {
    goTo
  } = useOverviewNavigation();
  const {
    isMobile
  } = useMediaQuery();
  const language = (languageFromProps || searchParamLanguage || getLang()).toUpperCase();
  const {
    lessonLoading: loading,
    lesson,
    lessonError: error
  } = useFullLessonChirp({
    lessonId: Number(lessonId)
  });
  const {
    allVideosHaveCaptions,
    loading: captionCheckLoading
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson === null || lesson === void 0 ? void 0 : lesson.content
  });
  const {
    hasGate: hasPreAssessmentGate
  } = useHasGate('Academy:LessonPreAssessment');
  const enrollToUrl = useMemo(() => {
    if (!lesson || captionCheckLoading || !allVideosHaveCaptions || !hasPreAssessmentGate || lesson.content.filter(isVideoAsset).length <= 1) {
      return undefined;
    }
    return getLessonPreAssessmentUrl(lessonId.toString());
  }, [lesson, captionCheckLoading, allVideosHaveCaptions, hasPreAssessmentGate, lessonId]);
  const availableLanguagesData = lesson && getLanguagesFromLinkedContent((_lesson$languageLinke = lesson.languageLinkedContent) === null || _lesson$languageLinke === void 0 ? void 0 : _lesson$languageLinke.languageToId, lesson.language);
  const deprecationCta = (lesson === null || lesson === void 0 ? void 0 : lesson.deprecationCta) || undefined;
  const hubspotDomain = getFullUrl('app', {
    hubletOverride: na1
  });
  const professors = useMemo(() => {
    if (!lesson) return [];
    const professorsById = {};
    lesson.content.forEach(asset => {
      if (asset.__typename === VideoAssetTypename) {
        asset.professors.forEach(professor => {
          professorsById[professor.id] = mapProfessorDescriptions(professor);
        });
      }
    });
    return Object.values(professorsById);
  }, [lesson]);
  const curriculumContent = useMemo(() => {
    if (!lesson) {
      return [];
    }
    return lesson.content.filter(content => content.__typename !== LinkAssetTypename);
  }, [lesson]);
  const handleChangeSelectedLanguage = ({
    target: {
      value
    }
  }) => {
    var _lesson$languageLinke2;
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'overview-modal-change-language',
      appearsIn: source,
      lessonId: lesson ? lesson.id : null,
      name: lesson ? lesson.name : null,
      previousLanguage: language,
      newLanguage: value
    });
    const targetId = (lesson === null || lesson === void 0 || (_lesson$languageLinke2 = lesson.languageLinkedContent) === null || _lesson$languageLinke2 === void 0 || (_lesson$languageLinke2 = _lesson$languageLinke2.languageToId) === null || _lesson$languageLinke2 === void 0 ? void 0 : _lesson$languageLinke2[value.toUpperCase()]) || lessonId;
    goTo({
      id: targetId,
      type: 'LESSON',
      language: value
    });
  };
  const handleEnroll = () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'overview-modal-enroll',
    appearsIn: source,
    contentLanguage: language,
    contentType: 'LESSON',
    lessonId: lesson ? lesson.id : null,
    name: lesson ? lesson.name : null,
    state: enrollToUrl ? 'pre_assessment_available' : 'pre_assessment_unavailable'
  });
  const handleContentClick = (content, index) => () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'content-card-click',
    contentType: 'ASSET',
    assetId: content.id,
    appearsIn: 'lesson-overview-curriculum',
    position: index + 1,
    name: content.name,
    style: 'reduced',
    language
  });
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'professor-organisation-click',
    contentType: 'LESSON',
    appearsIn: 'lesson-overview-curriculum',
    organisation,
    organisationUrl,
    professorId,
    lessonId
  });
  const getContentThumbnail = content => {
    if (content.__typename === VideoAssetTypename) {
      return `${hubspotDomain}/api/video/v1/public/${content.reference}/poster?portalId=${content.hubId}`;
    }
    return content.previewThumbnailSquareUrl || getFallbackThumbnailUrl(content.__typename);
  };
  const getCurriculumDuration = content => new Date().getTime() - ('duration' in content ? content.duration || 0 : 0) * 1000;
  const lessonCardModel = maybeLessonCardModelWithProgress({
    lesson
  });
  return /*#__PURE__*/_jsx(OverviewContent, {
    assetLanguage: language,
    backgroundColor: isPlaylistOverview ? 'fill-surface-default' : 'fill-surface-recessed',
    deprecated: !!deprecationCta,
    deprecationCta: deprecationCta,
    error: unwrapLessonChirpError(error),
    fallbackThumbnailUrl: lesson && lesson.previewThumbnailRectangleUrl,
    headerIcons: lessonCardModel && /*#__PURE__*/_jsx(CardMenuIcon, {
      item: lessonCardModel
    }),
    isInternalContent: lessonCardModel ? isInternalContent(lessonCardModel) : false,
    lessonId: lessonId.toString(),
    loading: loading,
    children: /*#__PURE__*/_jsxs(View, {
      paddingInline: isMobile ? '300' : '800',
      children: [/*#__PURE__*/_jsx(OverviewModalDescriptionSection, {
        availableLanguages: availableLanguagesData,
        contentList: lesson === null || lesson === void 0 ? void 0 : lesson.content,
        description: lesson && lesson.description,
        duration: lesson && /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().getTime() - (lesson.duration || 0) * 1000
        }),
        enrollToUrl: enrollToUrl,
        hasStudyGuide: false,
        id: lesson === null || lesson === void 0 ? void 0 : lesson.id,
        isPlaylistOverview: isPlaylistOverview,
        onChangeSelectedLanguage: handleChangeSelectedLanguage,
        onEnroll: handleEnroll,
        selectedLanguage: language,
        title: lesson && lesson.name,
        type: "LESSON"
      }), !!professors.length && /*#__PURE__*/_jsx(InstructorList, {
        backgroundColor: isPlaylistOverview ? 'fill-surface-recessed' : undefined,
        borderRadius: isPlaylistOverview ? '300' : undefined,
        handleOrganisationUrlClick: handleOrganisationUrlClick,
        language: language,
        marginBlockStart: isPlaylistOverview ? '600' : undefined,
        padding: isPlaylistOverview ? '800' : undefined,
        professors: professors
      }), !!curriculumContent.length && /*#__PURE__*/_jsx(OverviewModalCurriculumSection, {
        isPlaylistOverview: isPlaylistOverview,
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          gap: "600",
          marginBlockStart: "500",
          children: curriculumContent.map((content, index) => /*#__PURE__*/_jsx(CardWrapper, {
            marginBlockEnd: "0",
            paddingBlock: "400",
            paddingInline: "800",
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: /*#__PURE__*/_jsx(ACInlineContentCard, {
                description: /*#__PURE__*/_jsx(FormattedDuration, {
                  from: getCurriculumDuration(content)
                }),
                name: content.name,
                onClick: handleContentClick(content, index),
                size: "extra-small",
                thumbnailSrc: getContentThumbnail(content),
                to: `/lessons/${lessonId}/${content.id}?language=${language}`,
                use: "on-surface-recessed"
              })
            })
          }, content.id))
        })
      })]
    })
  });
};
export default LessonOverviewModalContainer;